import Player from "@vimeo/player";
import { trackEvent } from "@/utils/analytics/analytics";

export type VideoMeta = {
  video_id: number | string;
  video_title: string;
  content_type: string;
  video_category?: string;
};

export type VimeoAnalyticsEvents = {
  videoStartedEvent: string;
  videoProgressEvent: string;
  videoCompletedEvent: string;
  videoPausedEvent: string;
  videoSeekedEvent: string;
  videoCaptionsChangedEvent: string;
  videoFullscreenChangedEvent: string;
  videoEnterPictureInPictureEvent: string;
  videoLeavePictureInPictureEvent: string;
  videoQualityChangedEvent: string;
  videoPlaybackRateChangedEvent: string;
  videoVolumeChangedEvent: string;
  videoBufferStartEvent: string;
  videoBufferEndEvent: string;
};

const PERCENTAGE_WATCH_MILESTONES = [25, 50, 75] as const;

export const toMinutes = (seconds: number): number =>
  parseFloat((seconds / 60).toFixed(2));

/**
 * Internal helper that wires all analytics listeners onto an existing Player instance.
 * Both public entry points delegate here.
 */
const wireVimeoAnalytics = (
  player: Player,
  meta: VideoMeta,
  events: VimeoAnalyticsEvents,
): void => {
  let started = false;
  const milestones = new Set<number>();

  // Tracked via timeupdate so position-less events can include current playback state
  let lastKnownSeconds = 0;
  let lastKnownMinutes = 0;
  let lastKnownPercent = 0;

  let seekFromSeconds = 0;
  let seekFromMinutes = 0;
  let bufferStartTimestamp = 0;
  let volumeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // State guards — some Vimeo SDK events fire twice per state transition
  let lastFullscreenState: boolean | null = null;
  let lastCaptionsKey: string | null = null;
  let lastQuality: string | null = null;
  let lastPlaybackRate: number | null = null;
  let lastPictureInPictureState: boolean | null = null;

  const baseMeta = {
    video_id: meta.video_id,
    video_title: meta.video_title,
    content_type: meta.content_type,
    video_category: meta.video_category,
  };

  player.getDuration().then((duration: number) => {
    const durationSeconds = Math.round(duration);
    const durationMinutes = toMinutes(duration);

    player.on("play", () => {
      if (started) return;
      started = true;

      trackEvent(events.videoStartedEvent, {
        ...baseMeta,
        video_length_seconds: durationSeconds,
        video_length_minutes: durationMinutes,
      });
    });

    player.on(
      "timeupdate",
      ({ seconds, percent }: { seconds: number; percent: number }) => {
        lastKnownSeconds = seconds;
        lastKnownMinutes = toMinutes(seconds);
        lastKnownPercent = parseFloat((percent * 100).toFixed(1));

        const percentageWatched = Math.floor(percent * 100);
        PERCENTAGE_WATCH_MILESTONES.forEach((milestone) => {
          if (percentageWatched >= milestone && !milestones.has(milestone)) {
            milestones.add(milestone);

            trackEvent(events.videoProgressEvent, {
              ...baseMeta,
              progress_percent: milestone,
              progress_seconds: Math.round(seconds),
              progress_minutes: toMinutes(seconds),
              video_length_seconds: durationSeconds,
              video_length_minutes: durationMinutes,
            });
          }
        });
      },
    );

    player.on("ended", () => {
      trackEvent(events.videoCompletedEvent, {
        ...baseMeta,
        video_length_seconds: durationSeconds,
        video_length_minutes: durationMinutes,
      });
    });

    player.on(
      "pause",
      ({ seconds, percent }: { seconds: number; percent: number }) => {
        trackEvent(events.videoPausedEvent, {
          ...baseMeta,
          position_seconds: Math.round(seconds),
          position_minutes: toMinutes(seconds),
          position_percent: parseFloat((percent * 100).toFixed(1)),
          video_length_seconds: durationSeconds,
          video_length_minutes: durationMinutes,
        });
      },
    );

    player.on("seeking", ({ seconds }: { seconds: number }) => {
      seekFromSeconds = seconds;
      seekFromMinutes = toMinutes(seconds);
    });

    player.on(
      "seeked",
      ({ seconds, percent }: { seconds: number; percent: number }) => {
        trackEvent(events.videoSeekedEvent, {
          ...baseMeta,
          seek_from_seconds: Math.round(seekFromSeconds),
          seek_from_minutes: seekFromMinutes,
          seek_to_seconds: Math.round(seconds),
          seek_to_minutes: toMinutes(seconds),
          seek_to_percent: parseFloat((percent * 100).toFixed(1)),
          video_length_seconds: durationSeconds,
          video_length_minutes: durationMinutes,
        });
      },
    );

    player.on(
      "texttrackchange",
      (track: { kind: string; label: string; language: string }) => {
        const captionsKey = `${track.kind}:${track.language}:${track.label}`;
        if (captionsKey === lastCaptionsKey) return;
        lastCaptionsKey = captionsKey;
        trackEvent(events.videoCaptionsChangedEvent, {
          ...baseMeta,
          captions_kind: track.kind,
          captions_label: track.label,
          captions_language: track.language,
          position_seconds: Math.round(lastKnownSeconds),
          position_minutes: lastKnownMinutes,
          position_percent: lastKnownPercent,
        });
      },
    );

    player.on("fullscreenchange", ({ fullscreen }: { fullscreen: boolean }) => {
      if (fullscreen === lastFullscreenState) return;
      lastFullscreenState = fullscreen;
      trackEvent(events.videoFullscreenChangedEvent, {
        ...baseMeta,
        is_fullscreen: fullscreen,
        position_seconds: Math.round(lastKnownSeconds),
        position_minutes: lastKnownMinutes,
        position_percent: lastKnownPercent,
      });
    });

    player.on("enterpictureinpicture", () => {
      if (lastPictureInPictureState === true) return;
      lastPictureInPictureState = true;
      trackEvent(events.videoEnterPictureInPictureEvent, {
        ...baseMeta,
        position_seconds: Math.round(lastKnownSeconds),
        position_minutes: lastKnownMinutes,
        position_percent: lastKnownPercent,
      });
    });

    player.on("leavepictureinpicture", () => {
      if (lastPictureInPictureState === false) return;
      lastPictureInPictureState = false;
      trackEvent(events.videoLeavePictureInPictureEvent, {
        ...baseMeta,
        position_seconds: Math.round(lastKnownSeconds),
        position_minutes: lastKnownMinutes,
        position_percent: lastKnownPercent,
      });
    });

    player.on("qualitychange", ({ quality }: { quality: string }) => {
      if (quality === lastQuality) return;
      lastQuality = quality;
      trackEvent(events.videoQualityChangedEvent, {
        ...baseMeta,
        video_quality: quality,
        position_seconds: Math.round(lastKnownSeconds),
        position_minutes: lastKnownMinutes,
        position_percent: lastKnownPercent,
      });
    });

    player.on(
      "playbackratechange",
      ({ playbackRate }: { playbackRate: number }) => {
        if (playbackRate === lastPlaybackRate) return;
        lastPlaybackRate = playbackRate;
        trackEvent(events.videoPlaybackRateChangedEvent, {
          ...baseMeta,
          playback_rate: playbackRate,
          position_seconds: Math.round(lastKnownSeconds),
          position_minutes: lastKnownMinutes,
          position_percent: lastKnownPercent,
        });
      },
    );

    player.on("volumechange", ({ volume }: { volume: number }) => {
      if (volumeDebounceTimer) clearTimeout(volumeDebounceTimer);
      volumeDebounceTimer = setTimeout(() => {
        trackEvent(events.videoVolumeChangedEvent, {
          ...baseMeta,
          volume_level: volume,
          position_seconds: Math.round(lastKnownSeconds),
          position_minutes: lastKnownMinutes,
          position_percent: lastKnownPercent,
        });
      }, 500);
    });

    player.on("bufferstart", () => {
      bufferStartTimestamp = Date.now();
      trackEvent(events.videoBufferStartEvent, {
        ...baseMeta,
        position_seconds: Math.round(lastKnownSeconds),
        position_minutes: lastKnownMinutes,
        position_percent: lastKnownPercent,
      });
    });

    player.on("bufferend", () => {
      trackEvent(events.videoBufferEndEvent, {
        ...baseMeta,
        buffer_duration_milliseconds: bufferStartTimestamp
          ? Date.now() - bufferStartTimestamp
          : 0,
        position_seconds: Math.round(lastKnownSeconds),
        position_minutes: lastKnownMinutes,
        position_percent: lastKnownPercent,
      });
    });
  });
};

/**
 * Attaches Vimeo Player SDK analytics to an already-rendered Vimeo iframe.
 * Finds the iframe inside the container, creates a Player, and wires up analytics.
 *
 * Returns the Player instance so the caller can destroy it on unmount.
 * Returns null if no iframe is found inside the container.
 */
export const attachVimeoAnalytics = (
  container: HTMLElement,
  meta: VideoMeta,
  events: VimeoAnalyticsEvents,
): Player | null => {
  const iframe = container.querySelector("iframe");
  if (!iframe) return null;

  const player = new Player(iframe);
  wireVimeoAnalytics(player, meta, events);
  return player;
};

/**
 * Attaches analytics to an existing Vimeo Player instance (e.g. one created via `new Player(ref, { id })`).
 * Use this when the caller already owns the Player — no iframe lookup needed.
 */
export const attachVimeoAnalyticsToPlayer = (
  player: Player,
  meta: VideoMeta,
  events: VimeoAnalyticsEvents,
): void => {
  wireVimeoAnalytics(player, meta, events);
};
